import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  setDoc,
} from "firebase/firestore";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { db } from "../../firebase";
import { findUsersMissingAuth, createAuthUsersBatch } from "../../services/authUserService";
import "./admin.css";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [successMessage, setSuccessMessage] = useState("");
  const [viewMode, setViewMode] = useState("table"); // table or hierarchy
  const [showOrgChart, setShowOrgChart] = useState(false);
  const [selectedTeamRoot, setSelectedTeamRoot] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [showAuthFixModal, setShowAuthFixModal] = useState(false);
  const [usersMissingAuth, setUsersMissingAuth] = useState([]);
  const [fixingAuth, setFixingAuth] = useState(false);
  const [fixProgress, setFixProgress] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
    setCurrentPage(1); // Reset to first page when filters change
  }, [users, searchTerm, filterRole]);

  const fetchUsers = async () => {
    try {
      const usersCollection = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort by email or name
      usersList.sort((a, b) => {
        const nameA = (a.name || a.email || "").toLowerCase();
        const nameB = (b.name || b.email || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      alert("Error loading users. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          (user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.email || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filterRole !== "all") {
      filtered = filtered.filter(
        (user) => (user.plan || "New Agent") === filterRole
      );
    }

    setFilteredUsers(filtered);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      name: user.name || "",
      fullName: user.fullName || user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      role: user.role || "",
      plan: user.plan || "New Agent",
      recruiter: user.recruiter || "",
      leader: user.leader || "",
      photoUrl: user.photoUrl || "",
      agencyCode: user.agencyCode || "",
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setEditForm({});
  };

  const handleResetPassword = async (userEmail) => {
    if (!userEmail) {
      alert("User email is required to reset password.");
      return;
    }

    if (window.confirm(`Send password reset email to ${userEmail}?`)) {
      try {
        await sendPasswordResetEmail(auth, userEmail);
        showSuccess(`Password reset email sent to ${userEmail}`);
      } catch (error) {
        console.error("Error sending password reset:", error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleUpdate = async (userId) => {
    try {
      const userDoc = doc(db, "users", userId);
      await updateDoc(userDoc, editForm);
      handleCloseModal();
      await fetchUsers(); // Refresh the list
      showSuccess("User updated successfully! User should logout and login again to see changes.");
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Error updating user. Check console for details.");
    }
  };

  const handleFindMissingAuth = async () => {
    try {
      setFixingAuth(true);
      const missing = await findUsersMissingAuth();
      setUsersMissingAuth(missing);
      setShowAuthFixModal(true);
      setFixingAuth(false);
    } catch (error) {
      console.error("Error finding users:", error);
      alert(`Error: ${error.message}`);
      setFixingAuth(false);
    }
  };

  const handleFixAuthUsers = async () => {
    if (usersMissingAuth.length === 0) return;

    if (!window.confirm(`Create Auth accounts for ${usersMissingAuth.length} users? They will receive password setup emails.`)) {
      return;
    }

    try {
      setFixingAuth(true);
      setFixProgress({ current: 0, total: usersMissingAuth.length });

      const userIds = usersMissingAuth.map((u) => u.id);

      const results = await createAuthUsersBatch(userIds, (progress) => {
        setFixProgress(progress);
      });

      setFixingAuth(false);
      setFixProgress(null);

      // Refresh users list
      await fetchUsers();

      alert(`✅ Complete!\n\nSuccess: ${results.success}\nFailed: ${results.failed}\n\nCheck console for details.`);

      if (results.success > 0) {
        setShowAuthFixModal(false);
        showSuccess(`Created Auth accounts for ${results.success} users!`);
      }
    } catch (error) {
      console.error("Error fixing Auth users:", error);
      alert(`Error: ${error.message}`);
      setFixingAuth(false);
      setFixProgress(null);
    }
  };

  const buildHierarchy = () => {
    // Create a map for quick lookup
    const userMap = {};
    users.forEach((user) => {
      userMap[user.email || user.id] = { ...user, children: [] };
    });

    const roots = [];

    // Build the tree structure
    users.forEach((user) => {
      const currentUser = userMap[user.email || user.id];
      const plan = user.plan || "New Agent";
      
      // Only Brokers and Admins should be at root level
      const isBrokerOrAdmin = plan === "Broker" || plan === "Admin";
      
      if (isBrokerOrAdmin) {
        // Brokers and Admins always go to root
        roots.push(currentUser);
      } else if (user.recruiter && userMap[user.recruiter]) {
        // Everyone else goes under their recruiter if recruiter exists
        userMap[user.recruiter].children.push(currentUser);
      } else {
        // If no recruiter or recruiter doesn't exist, go to root
        roots.push(currentUser);
      }
    });

    return roots;
  };

  const handleDelete = async (userId) => {
    const user = users.find((u) => u.id === userId);
    const confirmMessage = `Are you sure you want to delete ${user?.name || user?.email || "this user"}? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteDoc(doc(db, "users", userId));
        await fetchUsers(); // Refresh the list
        showSuccess("User deleted successfully.");
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Error deleting user. Check console for details.");
      }
    }
  };

  // Pagination calculations
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <div className="content-stage">Loading users...</div>;
  }

  const handleOpenOrgChart = (rootNode) => {
    setSelectedTeamRoot(rootNode);
    setShowOrgChart(true);
  };

  const handleEditFromOrgChart = (user) => {
    setEditingUser(user.id);
    setEditForm({
      name: user.name || "",
      fullName: user.fullName || user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      role: user.role || "",
      plan: user.plan || "New Agent",
      recruiter: user.recruiter || "",
      leader: user.leader || "",
      photoUrl: user.photoUrl || "",
      agencyCode: user.agencyCode || "",
    });
    setShowModal(true);
  };

  const renderOrgChartNode = (node, isRoot = false) => {
    return (
      <div key={node.id} className="org-chart-node-wrapper">
        <div 
          className="org-chart-node" 
          onClick={() => handleEditFromOrgChart(node)}
          title="Click to edit"
        >
          <div className="org-node-avatar">
            {node.photoUrl ? (
              <img src={node.photoUrl} alt={node.name} />
            ) : (
              <div className="org-node-initials">
                {(node.name || node.email || "?").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="org-node-info">
            <div className="org-node-name">{node.name || node.email}</div>
            <div className="org-node-role">{node.plan || "New Agent"}</div>
            {node.phone && <div className="org-node-contact">{node.phone}</div>}
          </div>
          {node.children.length > 0 && (
            <div className="org-node-count">{node.children.length} recruit{node.children.length !== 1 ? 's' : ''}</div>
          )}
        </div>
        
        {node.children.length > 0 && (
          <div className="org-chart-children">
            <div className="org-chart-line" />
            <div className="org-chart-children-row">
              {node.children.map((child) => renderOrgChartNode(child))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHierarchyNode = (node, level = 0) => {
    return (
      <div key={node.id} className="hierarchy-node" style={{ marginLeft: `${level * 30}px` }}>
        <div className="hierarchy-card">
          <div className="hierarchy-info">
            <strong>{node.name || node.email}</strong>
            <span className="hierarchy-meta">
              {node.plan} {node.children.length > 0 && `• ${node.children.length} recruits`}
            </span>
          </div>
          <div className="hierarchy-actions">
            <button onClick={() => handleOpenOrgChart(node)} className="modern-btn primary small">
              View Team
            </button>
          </div>
        </div>
        {node.children.length > 0 && (
          <div className="hierarchy-children">
            {node.children.map((child) => renderHierarchyNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>User Management</h1>
          <p>Manage system users and their account types. Users must logout and login again after changes.</p>
        </div>
        <div className="admin-stats">
          <div className="stat-card">
            <span className="stat-value">{users.length}</span>
            <span className="stat-label">Total Users</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{filteredUsers.length}</span>
            <span className="stat-label">Filtered</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{currentPage} / {totalPages || 1}</span>
            <span className="stat-label">Page</span>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      <div className="admin-toolbar">
        <div className="admin-filters">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Filter by Account Type</label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="New Agent">New Agent (Free)</option>
              <option value="Agent">Agent</option>
              <option value="Leader">Leader</option>
              <option value="Broker">Broker</option>
              <option value="Admin">Admin</option>
            </select>
          </div>
        </div>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
            onClick={() => setViewMode("table")}
          >
            📋 Table View
          </button>
          <button
            className={`toggle-btn ${viewMode === "hierarchy" ? "active" : ""}`}
            onClick={() => setViewMode("hierarchy")}
          >
            🌳 Team Hierarchy
          </button>
          <button
            className="toggle-btn auth-fix-btn"
            onClick={handleFindMissingAuth}
            disabled={fixingAuth}
            title="Find users missing Firebase Auth accounts"
          >
            {fixingAuth ? "🔄 Checking..." : "🔐 Fix Auth Users"}
          </button>
        </div>
      </div>

      {viewMode === "table" ? (
        <div className="modern-table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Account Type</th>
                <th>Recruiter</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                    No users found
                  </td>
                </tr>
              ) : (
                currentUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name || user.fullName || "N/A"}</td>
                    <td>{user.email}</td>
                    <td>{user.phone || "—"}</td>
                    <td>
                      <span className={`account-badge ${(user.plan || "").toLowerCase().replace(" ", "-")}`}>
                        {user.plan || "New Agent"}
                      </span>
                    </td>
                    <td>{user.recruiter || "—"}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(user)}
                          className="modern-btn primary small"
                          title="Edit User"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleResetPassword(user.email)}
                          className="modern-btn secondary small"
                          title="Reset Password"
                        >
                          🔑
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="modern-btn danger small"
                          title="Delete User"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first, last, current, and pages around current
                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                    );
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const showEllipsis = index > 0 && page - array[index - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsis && <span className="pagination-ellipsis">...</span>}
                        <button
                          className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="hierarchy-container">
          <div className="hierarchy-header">
            <h3>Team Hierarchy</h3>
            <p>View your team structure based on recruiter relationships</p>
          </div>
          {buildHierarchy().map((root) => renderHierarchyNode(root))}
        </div>
      )}

      {/* Org Chart Modal */}
      {showOrgChart && selectedTeamRoot && (
        <div className="modal-overlay" onClick={() => setShowOrgChart(false)}>
          <div className="modal-content org-chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Team Organization Chart</h2>
                <p style={{ margin: "8px 0 0 0", color: "var(--muted)", fontSize: "0.9rem" }}>
                  Click any member to edit their information
                </p>
              </div>
              <button className="modal-close" onClick={() => setShowOrgChart(false)}>✕</button>
            </div>
            
            <div className="modal-body org-chart-container">
              {renderOrgChartNode(selectedTeamRoot, true)}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={editForm.fullName}
                    onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="form-input"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="form-group">
                  <label>Account Type</label>
                  <select
                    value={editForm.plan}
                    onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })}
                    className="form-input"
                  >
                    <option value="New Agent">New Agent (Free)</option>
                    <option value="Agent">Agent</option>
                    <option value="Leader">Leader</option>
                    <option value="Broker">Broker</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Agency Code</label>
                  <input
                    type="text"
                    value={editForm.agencyCode}
                    onChange={(e) => setEditForm({ ...editForm, agencyCode: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Recruiter (Email)</label>
                  <input
                    type="text"
                    value={editForm.recruiter}
                    onChange={(e) => setEditForm({ ...editForm, recruiter: e.target.value })}
                    className="form-input"
                    placeholder="recruiter@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Leader (Name)</label>
                  <input
                    type="text"
                    value={editForm.leader}
                    onChange={(e) => setEditForm({ ...editForm, leader: e.target.value })}
                    className="form-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Profile Photo URL</label>
                  <input
                    type="url"
                    value={editForm.photoUrl}
                    onChange={(e) => setEditForm({ ...editForm, photoUrl: e.target.value })}
                    className="form-input"
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={handleCloseModal} className="modern-btn secondary">
                Cancel
              </button>
              <button onClick={() => handleUpdate(editingUser)} className="modern-btn primary">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Fix Modal */}
      {showAuthFixModal && (
        <div className="modal-overlay" onClick={() => setShowAuthFixModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>🔐 Fix Firebase Authentication</h2>
                <p style={{ margin: "8px 0 0 0", color: "var(--muted)", fontSize: "0.9rem" }}>
                  These users have Firestore accounts but no Firebase Auth account
                </p>
              </div>
              <button className="modal-close" onClick={() => setShowAuthFixModal(false)}>✕</button>
            </div>
            
            <div className="modal-body" style={{ maxHeight: "400px", overflowY: "auto" }}>
              {usersMissingAuth.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
                  <div style={{ fontSize: "3rem", marginBottom: "16px" }}>✅</div>
                  <h3>All users have Auth accounts!</h3>
                  <p>Every user in Firestore has a corresponding Firebase Authentication account.</p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "20px", padding: "16px", background: "rgba(255, 215, 0, 0.1)", border: "1px solid rgba(255, 215, 0, 0.3)", borderRadius: "8px" }}>
                    <strong>⚠️ Found {usersMissingAuth.length} users without Auth accounts</strong>
                    <p style={{ margin: "8px 0 0 0", fontSize: "0.9rem", color: "var(--muted)" }}>
                      Click "Create Auth Accounts" below to automatically create Firebase Auth accounts for these users. They will each receive a password setup email.
                    </p>
                  </div>

                  <table className="modern-table" style={{ fontSize: "0.9rem" }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Account Type</th>
                        <th>Invited</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersMissingAuth.map((user) => (
                        <tr key={user.id}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            <span className={`account-badge ${user.plan?.toLowerCase().replace(" ", "-")}`}>
                              {user.plan || "Unknown"}
                            </span>
                          </td>
                          <td style={{ fontSize: "0.85rem", color: "var(--muted)" }}>
                            {user.invitedAt ? new Date(user.invitedAt.toDate()).toLocaleDateString() : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {fixProgress && (
                    <div style={{ marginTop: "20px", padding: "16px", background: "rgba(212, 175, 55, 0.1)", border: "1px solid rgba(212, 175, 55, 0.3)", borderRadius: "8px" }}>
                      <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <strong>Creating Auth accounts...</strong>
                        <span>{fixProgress.current} / {fixProgress.total}</span>
                      </div>
                      <div style={{ width: "100%", height: "8px", background: "rgba(0,0,0,0.2)", borderRadius: "4px", overflow: "hidden" }}>
                        <div 
                          style={{ 
                            width: `${(fixProgress.current / fixProgress.total) * 100}%`, 
                            height: "100%", 
                            background: "linear-gradient(90deg, #d4af37, #f5d66a)", 
                            transition: "width 0.3s ease" 
                          }} 
                        />
                      </div>
                      <div style={{ marginTop: "8px", fontSize: "0.85rem", color: "var(--muted)" }}>
                        Success: {fixProgress.success} • Failed: {fixProgress.failed}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="modal-footer">
              {usersMissingAuth.length > 0 && (
                <>
                  <button 
                    className="modern-btn primary" 
                    onClick={handleFixAuthUsers}
                    disabled={fixingAuth}
                  >
                    {fixingAuth ? "🔄 Creating..." : `🔐 Create Auth Accounts (${usersMissingAuth.length})`}
                  </button>
                  <button 
                    className="modern-btn ghost" 
                    onClick={() => setShowAuthFixModal(false)}
                    disabled={fixingAuth}
                  >
                    Cancel
                  </button>
                </>
              )}
              {usersMissingAuth.length === 0 && (
                <button className="modern-btn primary" onClick={() => setShowAuthFixModal(false)}>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
